import { useMemo, useState } from 'react'
import { useEnrollmentRequiredDocuments } from '@/api/enrollment-required-documents'
import {
  useEnrollmentRequirements,
  useUpdateEnrollmentRequirements,
  type EnrollmentRequirement,
  type EnrollmentRequirementValue
} from '@/api/enrollment-requirements'
import { useEnrollmentDocuments } from '@/api/enrollment-api'
import { useEnrollmentStore } from '@/stores'
import { getStoredEnrollmentLocationState } from '../../utils/enrollmentStorageSelectors'
import { getMvrUrl } from '../uploadDocumentConfig'
import {
  buildUploadedDocumentsByType,
  getUploadedDocumentFileName,
  getVisibleUploadDocuments,
  isUploadDocumentCompleted
} from './documentState'
import { getUploadDocumentsValidationError } from './helpers'
import type { VisibleUploadDocument } from './types'
import { useUploadDocumentUpload } from './useUploadDocumentUpload'
import { useUploadDocumentsAutoAdvance } from './useUploadDocumentsAutoAdvance'
import { useUploadDocumentsStoreSync } from './useUploadDocumentsStoreSync'

export function useUploadDocumentsStep(onNext: (options?: { skipUploadStep?: boolean }) => void) {
  const enrollmentSessionToken = useEnrollmentStore(state => state.sessionToken)
  const files = useEnrollmentStore(state => state.uploadedDocumentFiles)
  const storedLocationState = useEnrollmentStore(state => state.formData.locationState)
  const updateUploadedDocumentFiles = useEnrollmentStore(state => state.updateUploadedDocumentFiles)
  const documentsQuery = useEnrollmentDocuments(enrollmentSessionToken)
  const requiredDocumentsQuery = useEnrollmentRequiredDocuments(enrollmentSessionToken)
  const requirementsQuery = useEnrollmentRequirements(enrollmentSessionToken)
  const updateRequirements = useUpdateEnrollmentRequirements()
  const [validationError, setValidationError] = useState<string | null>(null)
  const [requirementsError, setRequirementsError] = useState<string | null>(null)
  const [requirementValues, setRequirementValues] = useState<
    Record<number, string | number | boolean | undefined>
  >({})

  const locationState = getStoredEnrollmentLocationState(storedLocationState)
  const normalizedState = locationState.toUpperCase()
  const mvrUrl = getMvrUrl(normalizedState)
  const visibleDocuments = getVisibleUploadDocuments(requiredDocumentsQuery.data)
  const incompleteFieldRequirements = useMemo(
    () =>
      (requirementsQuery.data ?? []).filter(
        requirement => requirement.requirementType === 'FIELD' && !requirement.completed
      ),
    [requirementsQuery.data]
  )

  const uploadedDocumentsByType = useMemo(
    () => buildUploadedDocumentsByType(documentsQuery.data),
    [documentsQuery.data]
  )

  useUploadDocumentsStoreSync({
    documents: documentsQuery.data,
    updateUploadedDocumentFiles
  })

  useUploadDocumentsAutoAdvance({
    isError: requiredDocumentsQuery.isError || requirementsQuery.isError,
    isLoading: requiredDocumentsQuery.isLoading || requirementsQuery.isLoading,
    onNext,
    visibleDocumentsCount: visibleDocuments.length + incompleteFieldRequirements.length
  })

  const { handleFileChange, isUploading, uploadError, uploadingKeySet } = useUploadDocumentUpload({
    enrollmentSessionToken,
    onUploadSuccess: () => {
      void documentsQuery.refetch()
      void requiredDocumentsQuery.refetch()
    },
    setValidationError,
    updateUploadedDocumentFiles
  })

  const handleRequirementChange = (requirementId: number, value: string | number | boolean) => {
    setRequirementValues(current => ({ ...current, [requirementId]: value }))
    setRequirementsError(null)
    setValidationError(null)
  }

  const hasRequirementValue = (requirement: EnrollmentRequirement) => {
    const value = requirementValues[requirement.id]
    if (requirement.inputType === 'CHECKBOX') return typeof value === 'boolean'
    if (requirement.inputType === 'NUMBER') return typeof value === 'number' && !Number.isNaN(value)
    return typeof value === 'string' && value.trim().length > 0
  }

  const requirementPayload = (requirement: EnrollmentRequirement): EnrollmentRequirementValue => {
    const value = requirementValues[requirement.id]
    const payload: EnrollmentRequirementValue = { requirementId: requirement.id }
    if (requirement.inputType === 'NUMBER' && typeof value === 'number') payload.numberValue = value
    else if (requirement.inputType === 'DATE' && typeof value === 'string')
      payload.dateValue = value
    else if (requirement.inputType === 'CHECKBOX' && typeof value === 'boolean') {
      payload.checkboxValue = value
    } else if (typeof value === 'string') payload.textValue = value
    return payload
  }

  const handleContinue = async () => {
    if (requirementsQuery.isLoading || requirementsQuery.isError) {
      setRequirementsError('Partner information could not be loaded. Please refresh and try again.')
      return
    }
    const missingFields = incompleteFieldRequirements.filter(
      requirement => requirement.required && !hasRequirementValue(requirement)
    )
    if (missingFields.length > 0) {
      setRequirementsError(
        `Please complete: ${missingFields.map(requirement => requirement.label).join(', ')}.`
      )
      return
    }

    const nextValidationError = getUploadDocumentsValidationError({
      isLoading: requiredDocumentsQuery.isLoading,
      isError: requiredDocumentsQuery.isError,
      visibleDocuments,
      isDocumentUploaded: document => isUploadDocumentCompleted(document, uploadedDocumentsByType)
    })

    if (nextValidationError) {
      setValidationError(nextValidationError)
      return
    }

    if (incompleteFieldRequirements.length > 0 && enrollmentSessionToken) {
      try {
        await updateRequirements.mutateAsync({
          sessionToken: enrollmentSessionToken,
          values: incompleteFieldRequirements
            .filter(requirement => hasRequirementValue(requirement))
            .map(requirementPayload)
        })
        await requirementsQuery.refetch()
      } catch (error) {
        setRequirementsError(
          error instanceof Error
            ? error.message
            : 'Partner information could not be saved. Please try again.'
        )
        return
      }
    }

    setRequirementsError(null)
    setValidationError(null)
    onNext()
  }

  return {
    documentsQuery,
    getFileName: (document: VisibleUploadDocument) =>
      getUploadedDocumentFileName({ document, files, uploadedDocumentsByType }),
    handleContinue,
    handleFileChange,
    isUploading,
    isSavingRequirements: updateRequirements.isPending,
    mvrUrl,
    requiredDocumentsQuery,
    requirementsError,
    requirementsQuery,
    requirementValues,
    incompleteFieldRequirements,
    handleRequirementChange,
    uploadError,
    uploadingKeySet,
    validationError,
    visibleDocuments
  }
}
